import { useContext, useEffect, useState, useMemo } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  format,
  parseISO,
  differenceInYears,
  addMinutes,
  isWithinInterval,
} from 'date-fns'

import { Box, Button, Select, Grid, TextField } from '@mui/material'
// import Grid2 from '@mui/material/Grid2'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import Typography from '@mui/material/Typography'

import google from 'assets/images/logos/google_meets.png'
import teams from 'assets/images/logos/ms_teams.png'
import zoom from 'assets/images/logos/zoom.png'
import meeting from 'assets/images/icons/meeting-background.svg'

// Soft UI Dashboard PRO React components
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import OnboardingMultiOptionTile from './OnboardingMultiOptionTile'
import { selectData } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'

function generateEasternTimes() {
  const times = []

  for (let i = 0; i < 96; i++) {
    const minutes = i * 15
    const hour24 = Math.floor(minutes / 60)
    const minute = minutes % 60

    // Convert 24h → 12h time manually
    let period = 'AM'
    let hour12 = hour24

    if (hour24 === 0) hour12 = 12
    else if (hour24 === 12) period = 'PM'
    else if (hour24 > 12) {
      hour12 = hour24 - 12
      period = 'PM'
    }

    const label = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`

    times.push({
      label, // "12:00 AM" → "11:45 PM"
      hour: hour24,
      minute,
    })
  }

  return times
}

function getEasternNow() {
  const now = new Date()

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now)

  const get = (type) => Number(parts.find((p) => p.type === type).value)

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  }
}

function mergeDateAndEasternTime(dateStr, hour, minute) {
  const [y, m, d] = dateStr.split('-').map(Number)

  // Step 1: Make a Date in Eastern safely (via UTC first)
  const easternUtcGuess = Date.UTC(y, m - 1, d, hour, minute, 0)

  // Step 2: Find out the ACTUAL Eastern offset (EST or EDT)
  const actualEasternOffsetMinutes = -new Date(easternUtcGuess)
    .toLocaleString('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    })
    .includes('EDT')
    ? 240 // EDT = UTC-4
    : 300 // EST = UTC-5

  const offsetMs = actualEasternOffsetMinutes * 60 * 1000

  // Step 3: Build final UTC timestamp
  const finalUtc = new Date(easternUtcGuess + offsetMs)

  return finalUtc.toISOString()
}

function parseLocalDateString(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const AppointmentScheduler = ({
  closeModal = () => console.log('Schedule Appointment'),
  isAdmin = false,
  isOnPageForm = false,
}) => {
  // console.log('isAdmin', isAdmin)
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false)
  const [data, setData] = useState(null)
  const [availableDates, setAvailableDates] = useState(null)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [selectedRep, setSelectedRep] = useState(null)

  const availableTimes = useMemo(() => {
    const allTimes = generateEasternTimes()

    if (!selectedDate) return allTimes

    // Get today's date in Eastern time
    const eastNow = getEasternNow()

    const [year, month, day] = selectedDate.split('-').map(Number)
    const isToday =
      eastNow.year === year && eastNow.month === month && eastNow.day === day

    if (!isToday) return allTimes

    // Convert current Eastern time to minutes since midnight
    const nowMinutes = eastNow.hour * 60 + eastNow.minute

    // Filter out times earlier than the Eastern "now"
    return allTimes.filter((t) => {
      const tMinutes = t.hour * 60 + t.minute
      return tMinutes > nowMinutes
    })
  }, [selectedDate])
  // const availableTimes = useMemo(() => {
  //   return generateEasternTimes()
  // }, [selectedDate])
  // console.log('availableTimes', availableTimes)

  useEffect(() => {
    setSelectedTime(null) // Reset the selected time when date changes
  }, [selectedDate])

  const missingFields = []

  if (!intakeState?.salesforceAccountID)
    missingFields.push('SalesForce Account ID')
  if (!selectedDate) missingFields.push('Date')
  if (!selectedTime) missingFields.push('Time')
  if (!selectedRep) missingFields.push('Sales Rep')
  if (!selectedType) missingFields.push('Type')
  if (!selectedPlatform) missingFields.push('Platform')

  const disabledSchedule = missingFields.length > 0

  // Helper to format list naturally (with commas and "and")
  const formatList = (list) => {
    if (list.length === 1) return list[0]
    if (list.length === 2) return list.join(' and ')
    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`
  }

  const sendApptToSchedule = () => {
    const iso = mergeDateAndEasternTime(
      selectedDate,
      selectedTime.hour,
      selectedTime.minute
    )

    const eventToSchedule = {
      firstName: intakeState?.firstName || 'No First Name',
      lastName: intakeState?.lastName || 'No Last Name',
      email: intakeState?.email || 'No Email',
      phone: intakeState?.phone || 'No Phone',
      leadId: intakeState?.salesforceLeadID || 'No Lead ID',
      accountId: intakeState?.salesforceAccountID || 'No Account ID',
      scheduleWith: selectData.retirementStrategist.find(
        (s) => s.value === selectedRep
      ),
      selectedDate:
        format(parseLocalDateString(selectedDate), 'MM/dd/yyyy') || '',
      selectedTime: selectedTime.label + ' EST' || '',
      dateTimeISO: iso || '',
      selectedPlatform: selectedPlatform || '',
      meetingTypeWithduration:
        selectData.appointmentTypes.find((m) => m.value === selectedType) || '',
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/uit8xup/',
      payload: {
        eventToSchedule: eventToSchedule,
      },
      notificationMessage: `Appointment on ${format(
        parseLocalDateString(selectedDate),
        'MM/dd/yyyy'
      )} at ${selectedTime.label} EST is being scheduled!`,
      sendNotification: true,
    })
    setAppointmentConfirmed(true)
    // closeModal()
  }

  const resetForm = () => {
    setSelectedDate(null)
    setSelectedTime(null)
    setSelectedPlatform(null)
    setSelectedType(null)
    setSelectedRep(null)
    setAppointmentConfirmed(false)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid sx={{ textAlign: 'left' }} px={2} py={2}>
        {!appointmentConfirmed ? (
          <>
            <Box mb={1}>
              {/* <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
                Schedule an Appointment
              </Typography> */}
            </Box>
            <Box component="form">
              <Grid
                container
                xs={12}
                columns={1}
                mb={2}
                flexDirection={'column'}
                justifyContent="center"
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    '& .MuiDayCalendar-weekDayLabel': {
                      margin: '0px 0.5vw',
                    },
                    '& .MuiPickersDay-root': {
                      fontWeight: 600,
                      margin: '0px 0.5vw',
                    },
                    '& .MuiPickersDay-root.Mui-disabled': {
                      fontWeight: 400,
                    },
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: '#0671AD',
                    },
                    '& .MuiPickersDay-root.Mui-selected:active, & .MuiPickersDay-root.Mui-selected:focus, & .MuiPickersDay-root.Mui-selected:hover':
                      {
                        backgroundColor: '#045a8a',
                      },
                    '& .MuiPickersDay-root:hover': {
                      backgroundColor: '#e6f0f6',
                    },
                    '& .MuiPickersCalendarHeader-labelContainer': {
                      position: 'absolute',
                      left: '50%',
                      transform: 'translate(-50%)',
                    },
                    '& .MuiPickersArrowSwitcher-root': {
                      justifyContent: 'space-between',
                      width: '100%',
                    },
                    '& .MuiPickersCalendarHeader-root': {
                      position: 'relative',
                    },
                  }}
                >
                  <Typography variant="subtitle1">
                    Select A Meeting Date
                  </Typography>

                  <DateCalendar
                    sx={{ width: '100%' }}
                    shouldDisableDate={(date) => {
                      const today = new Date()
                      today.setHours(0, 0, 0, 0) // Normalize today's date (remove time part)

                      const twoMonthsLater = new Date()
                      twoMonthsLater.setMonth(today.getMonth() + 2)

                      const dateString = format(date, 'yyyy-MM-dd')

                      return (
                        date < today || date > twoMonthsLater
                        // (availableDates && !availableDates.has(dateString))
                      ) // Disable less than today, older than 2 months and dates with no availability
                    }}
                    value={selectedDate ? parseISO(selectedDate) : null}
                    onChange={(newValue) => {
                      setSelectedDate(format(newValue, 'yyyy-MM-dd'))

                      setSelectedTime(null)
                    }}
                    views={['day']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Select a Calendar, Time, and Type
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {renderMuiFormField({
                    key: selectedDate,
                    autoFocus: false,
                    label: 'Schedule With',
                    placeholder: 'Schedule With',
                    inputType: 'select',
                    value: selectedRep,
                    onChange: (e) => setSelectedRep(e),
                    options: selectData.retirementStrategist,
                  })}
                </Grid>
                <Grid item xs={12}>
                  {renderMuiFormField({
                    key: selectedDate,
                    autoFocus: false,
                    label: 'Meeting Time',
                    placeholder: 'Meeting Time',
                    inputType: 'select',
                    value: selectedTime,
                    onChange: (time) => setSelectedTime(time),
                    options: availableTimes.map((time) => ({
                      value: time,
                      label: `${time.label} EST`,
                    })),
                  })}
                </Grid>
                <Grid item xs={12}>
                  {renderMuiFormField({
                    key: selectedType,
                    autoFocus: false,
                    label: 'Appointment Type',
                    placeholder: 'Appointment Type',
                    inputType: 'select',
                    value: selectedType,
                    onChange: (e) => setSelectedType(e),
                    options: selectData.appointmentTypes,
                  })}
                </Grid>
                <Grid item xs={12} flexDirection={'row'}>
                  <OnboardingMultiOptionTile
                    header={' '}
                    helpText={''}
                    subHeader={'Select a Meeting Platform'}
                    expectedDataType={'string'}
                    tileSize={{ xs: '4' }}
                    onChange={(e) => {
                      setSelectedPlatform(e)
                    }}
                    optionArray={[
                      {
                        optionLabel: 'Google Meet',
                        optionValue: 'google_meet_retirement_consultation',
                        optionIcon: <img src={google}></img>,
                      },
                      {
                        optionLabel: 'Zoom',
                        optionValue: 'zoom_retirement_consultation',
                        optionIcon: <img src={zoom}></img>,
                      },
                      {
                        optionLabel: 'Microsoft Teams',
                        optionValue: 'ms_teams_retirement_consultation',
                        optionIcon: <img src={teams}></img>,
                      },
                    ]}
                  />
                </Grid>

                <Button
                  variant="parcoRounded"
                  disabled={disabledSchedule}
                  onClick={sendApptToSchedule}
                >
                  Confirm Appointment
                </Button>
                {disabledSchedule && (
                  <Typography
                    variant="inputNote"
                    component="p"
                    mt={1}
                    sx={{ color: 'red' }}
                  >
                    You are missing the following required field
                    {missingFields.length > 1 ? 's' : ''}:{' '}
                    {formatList(missingFields)}.
                  </Typography>
                )}
              </Grid>
            </Box>
          </>
        ) : (
          <>
            <Box textAlign="center" mt={4}>
              <Typography variant="h5" mb={2}>
                🎉 Appointment Confirmed!
              </Typography>
              <Typography variant="body1" mb={4}>
                Your appointment on{' '}
                {format(parseLocalDateString(selectedDate), 'MM/dd/yyyy')} at{' '}
                {selectedTime?.label || 'No Time Selected'} EST has been
                scheduled.
              </Typography>
              {!isOnPageForm && (
                <Button variant="parcoRounded" onClick={resetForm}>
                  Reset
                </Button>
              )}
            </Box>
          </>
        )}
      </Grid>
    </LocalizationProvider>
  )
}

export default AppointmentScheduler
